/**
 * Single Paper Journal admin surface: upload + upload history + participant row lookup.
 */

import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PaperJournalList from '@/pages/PaperJournal/PaperJournalList';
import { PaperJournalRowsLookup } from '@/pages/PaperJournal/PaperJournalRowsLookup/PaperJournalRowsLookup';

import './PaperJournalPage.scss';

const ROW_LOOKUP_SECTION_ID = 'paper-journal-row-lookup';

function _shouldScrollToLookup(params: URLSearchParams): boolean {
  const section = params.get('section');
  if (section === 'lookup') {
    return true;
  }
  // Legacy deep link from merged menu item.
  return params.get('tab') === 'search';
}

export const PaperJournalPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!_shouldScrollToLookup(searchParams)) {
      return;
    }
    const el = document.getElementById(ROW_LOOKUP_SECTION_ID);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [searchParams]);

  return (
    <div className="paper-journal-page">
      <header className="paper-journal-page__header">
        <h1 className="paper-journal-page__title">Paper Journal</h1>
        <p className="paper-journal-page__subtitle">
          Upload CSV files, review upload history, and look up journal rows by rewards user or
          eligible participant id.
        </p>
      </header>

      <section className="paper-journal-page__section" aria-labelledby="pj-uploads-heading">
        <h2 id="pj-uploads-heading" className="paper-journal-page__section-title">
          Uploads &amp; history
        </h2>
        <PaperJournalList embedded />
      </section>

      <section
        id={ROW_LOOKUP_SECTION_ID}
        className="paper-journal-page__section paper-journal-page__section--lookup"
        aria-labelledby="pj-lookup-heading"
      >
        <h2 id="pj-lookup-heading" className="paper-journal-page__section-title">
          Look up rows by participant
        </h2>
        <p className="paper-journal-page__section-lede">
          Search durable journal rows by <strong>ref user id</strong> or{' '}
          <strong>eligible participant id</strong>. This is not the same as filtering upload
          history by who uploaded the file.
        </p>
        <PaperJournalRowsLookup embedded />
      </section>
    </div>
  );
};

export default PaperJournalPage;
